import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMedia } from "@/lib/site";
import { useAuth } from "@/lib/auth";

export default function Discover() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/creators${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setCreators(data.creators);
      if (user?.type === "fan") {
        const f = await api.get("/fan/following").catch(() => ({ data: { ids: [] } }));
        setFollowingIds(new Set(f.data.ids || []));
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const follow = async (id) => {
    if (!user) return (window.location.href = "/signup?type=fan");
    try {
      await api.post(`/fan/follow/${id}`);
      setFollowingIds(new Set([...followingIds, id]));
      toast.success("Following");
    } catch { toast.error("Could not follow"); }
  };

  return (
    <div className="pt-28 pb-20 max-w-7xl mx-auto px-6" data-testid="discover">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl md:text-5xl text-white">Discover Creators</h1>
          <p className="text-white/60 mt-2">Fresh finds picked for your interests.</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&load()}
            placeholder="Search creators…" data-testid="discover-search"
            className="w-full bg-[#0f0819] border border-white/15 rounded-lg pl-10 pr-3 py-2.5 text-white placeholder:text-white/40 focus:outline-none focus:border-brand/50" />
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-brand" /></div>
      ) : creators.length === 0 ? (
        <p className="text-white/60">No creators found. Try a different search.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {creators.map((c) => (
            <div key={c.id} className="card overflow-hidden group" data-testid={`creator-${c.handle}`}>
              <Link to={`/@${c.handle.replace('@','')}`} className="block relative aspect-[4/5] overflow-hidden">
                <img src={resolveMedia(c.avatar || "/images/leather-hero.jpg")} alt={c.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-5">
                  <div className="text-white font-semibold text-lg">{c.name}</div>
                  <div className="text-white/60 text-xs mt-0.5">{c.handle}</div>
                </div>
              </Link>
              <div className="p-4 flex items-center justify-between">
                <div className="flex flex-wrap gap-1.5">
                  {(c.interests || []).slice(0, 2).map((i) => (
                    <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-white/5 text-white/60 uppercase tracking-wide">{i}</span>
                  ))}
                </div>
                <button onClick={() => follow(c.id)} disabled={followingIds.has(c.id)} data-testid={`follow-${c.handle}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${followingIds.has(c.id) ? "bg-white/10 text-white/60" : "bg-brand text-white hover:opacity-90"}`}>
                  <Heart size={13} fill={followingIds.has(c.id)?"currentColor":"none"} /> {followingIds.has(c.id) ? "Following" : "Follow"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
